import { useState, useEffect } from 'react';
import { getSettings, saveSettings, getSetting, updateSetting } from '../utils/settings';

/**
 * Custom hook for managing settings with real-time updates
 * @returns {Object} Settings state and utility functions
 */
export const useSettings = () => {
  const [settings, setSettingsState] = useState(() => getSettings());

  // Listen for storage changes to keep settings synchronized across components
  useEffect(() => {
    const handleStorageChange = () => {
      setSettingsState(getSettings());
    };

    // Listen for manual storage events (when settings are updated from other components)
    window.addEventListener('settings-updated', handleStorageChange);
    
    // Listen for localStorage changes (when settings are updated from other tabs)
    window.addEventListener('storage', (e) => {
      if (e.key === 'glyder_app_settings') {
        handleStorageChange();
      }
    });

    return () => {
      window.removeEventListener('settings-updated', handleStorageChange);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  const updateSettings = (newSettings) => {
    const updatedSettings = saveSettings(newSettings);
    setSettingsState(updatedSettings);
    
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('settings-updated'));
    
    return updatedSettings;
  };

  const updateSingleSetting = (key, value) => {
    const updatedSettings = updateSetting(key, value);
    setSettingsState(updatedSettings);
    
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('settings-updated'));
    
    return updatedSettings;
  };

  const getSingleSetting = (key) => {
    return settings[key];
  };

  return {
    settings,
    updateSettings,
    updateSingleSetting,
    getSingleSetting,
    // Convenience accessors for commonly used settings
    lotSize: settings.lotSize,
    defaultStopLossPercentage: settings.defaultStopLossPercentage,
    defaultTargetPercentage: settings.defaultTargetPercentage,
    autoRefreshInterval: settings.autoRefreshInterval,
    positionSizingStrategy: settings.positionSizingStrategy,
    maxMarginUtilization: settings.maxMarginUtilization,
    defaultRiskPercentage: settings.defaultRiskPercentage,
  };
};

/**
 * Hook for getting a single setting value with real-time updates
 * @param {string} settingKey - The setting key to watch
 * @returns {any} The setting value
 */
export const useSetting = (settingKey) => {
  const [value, setValue] = useState(() => getSetting(settingKey));

  useEffect(() => {
    const handleSettingsUpdate = () => {
      setValue(getSetting(settingKey));
    };

    window.addEventListener('settings-updated', handleSettingsUpdate);
    window.addEventListener('storage', (e) => {
      if (e.key === 'glyder_app_settings') {
        handleSettingsUpdate();
      }
    });

    return () => {
      window.removeEventListener('settings-updated', handleSettingsUpdate);
      window.removeEventListener('storage', handleSettingsUpdate);
    };
  }, [settingKey]);

  return value;
};

export default useSettings;
